import { useMemo, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { parseRegister, partitionRegister } from '../lib/parse-register';
import { Banner } from './Banner';
import { formatPhone } from '../lib/phone';
import { SlipButton, slipFor } from './SlipButton';
import type { GroupSummary } from './GroupBar';
import { RosterField, type RosterChoice } from './RosterField';

interface Created {
  id: string;
  fullName: string;
  phone: string;
  candidateNumber: string | null;
  password: string;
}

interface Outcome {
  created: Created[];
  skipped: { row: number; phone: string; reason: string }[];
  groupName?: string | null;
  instructorId?: string | null;
}

// Codes first, because that is the way to enrol a class without giving Target
// anybody's name or phone number; a name with a number still works.
const EXAMPLE = `S-017
S-018
Malika Rasulova, 91 234 56 78`;

/**
 * Enrolling a whole class from a pasted register.
 *
 * The register is parsed in the browser so the teacher sees the rows that will
 * be created before any are, and can fix a typo in the box rather than in a
 * list of failures afterwards.
 */
export function BulkEnrol({
  groups = [],
  organizationId,
  onDone,
}: {
  /** The classes this centre already teaches, to complete from. */
  groups?: GroupSummary[];
  /** The centre being worked inside; a platform administrator must name one. */
  organizationId?: string | null;
  onDone: () => void;
}) {
  const [text, setText] = useState('');
  const [groupName, setGroupName] = useState('');
  /** One paste is one register, and one register goes to one teacher. */
  const [enrolTo, setEnrolTo] = useState<RosterChoice>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [copied, setCopied] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const rows = useMemo(() => parseRegister(text), [text]);
  const { ready, broken } = useMemo(() => partitionRegister(rows), [rows]);

  async function enrol() {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<Outcome>('/users/bulk', {
        students: ready.map((r) => ({
          fullName: r.fullName,
          phone: r.phone,
          ...(r.password ? { password: r.password } : {}),
        })),
        // One paste is one class: a register is a class list, and asking for
        // the name once beats a third column nobody's spreadsheet has.
        groupName: groupName.trim() || null,
        instructorId: enrolTo,
      });
      setOutcome(data);
      setText('');
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not enrol that class'));
    } finally {
      setBusy(false);
    }
  }

  /** The one moment these passwords exist in readable form. */
  const credentialsText = (created: Created[]) =>
    created
      .map((c) => `${c.fullName}\t${formatPhone(c.phone)}\t${c.candidateNumber ?? ''}\t${c.password}`)
      .join('\n');

  if (outcome) {
    return (
      <section className="panel stack rise">
        <div>
          <p className="eyebrow">Enrolled</p>
          <h2 className="panel__title" style={{ marginBottom: 'var(--s-2)' }}>
            {outcome.created.length} student{outcome.created.length === 1 ? '' : 's'} added
            {outcome.groupName ? ` to ${outcome.groupName}` : ''}
          </h2>
          <p className="muted" style={{ maxWidth: '60ch' }}>
            Download the sign-in slips now and print them — one per student, ten to a page. The
            passwords are stored only as hashes and cannot be shown again; if a slip is lost, print
            a new one from the list below.
          </p>
        </div>

        {outcome.created.length > 0 && (
          <>
            <div className="ledger__wrap">
              <table className="ledger">
                <thead>
                  <tr>
                    <th className="num">Candidate</th>
                    <th>Name or code</th>
                    <th>Signs in with</th>
                    <th>Password</th>
                  </tr>
                </thead>
                <tbody>
                  {outcome.created.map((c) => (
                    <tr key={c.id}>
                      <td className="num">{c.candidateNumber ?? '—'}</td>
                      <td style={{ fontWeight: 600 }}>{c.fullName}</td>
                      <td className="mono muted">{formatPhone(c.phone)}</td>
                      <td className="mono" style={{ userSelect: 'all' }}>
                        {c.password}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row">
              <SlipButton
                primary
                slips={outcome.created.map(slipFor)}
                filename={`sign-in-slips${outcome.groupName ? `-${outcome.groupName.replace(/\W+/g, '-').toLowerCase()}` : ''}.pdf`}
                label={`Download ${outcome.created.length} sign-in slip${outcome.created.length === 1 ? '' : 's'} (PDF)`}
              />
              <button
                className="btn"
                onClick={() =>
                  void navigator.clipboard
                    ?.writeText(credentialsText(outcome.created))
                    .then(() => setCopied(true))
                }
              >
                {copied ? 'Copied' : 'Copy all as a table'}
              </button>
            </div>
          </>
        )}

        {outcome.skipped.length > 0 && (
          <div>
            <p className="eyebrow" style={{ marginBottom: 'var(--s-2)' }}>
              {outcome.skipped.length} not added
            </p>
            <ul className="diaglist">
              {outcome.skipped.map((s) => (
                <li key={`${s.row}-${s.phone}`} className="diaglist__item diaglist__item--warning">
                  <span className="mono diaglist__line">line {s.row}</span>
                  <span>
                    {s.phone || '(no number)'} — {s.reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="row">
          <button className="btn btn--primary" onClick={() => setOutcome(null)}>
            Done
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel stack rise">
      <div>
        <h2 className="panel__title">Enrol a class</h2>
        <p className="muted" style={{ maxWidth: '62ch' }}>
          Paste your register — one student per line: a name or a code such as{' '}
          <span className="mono">S-017</span>, and optionally a sign-in number, in either order. A
          comma, a tab or a semicolon all work, so a paste straight out of a spreadsheet is fine.
          Leave the number out and the student signs in with the candidate number they are given —
          then nobody's phone number reaches Target. A number need not be a real phone. A password
          is generated for each student unless you add one as a third column.
        </p>
      </div>

      {error && <Banner tone="error">{error}</Banner>}

      <div className="field">
        <label className="field__label" htmlFor="bulk-class">
          Class
        </label>
        <input
          id="bulk-class"
          className="field__input"
          list="bulk-classes"
          style={{ maxWidth: '22rem' }}
          maxLength={120}
          placeholder="Monday evening"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
        />
        <datalist id="bulk-classes">
          {groups.map((group) => (
            <option key={group.name} value={group.name} />
          ))}
        </datalist>
        <p className="field__hint">
          Optional. Everybody on this register goes into it, and the whole class can then be set a
          test in one go.
        </p>
      </div>

      <RosterField value={enrolTo} onChange={setEnrolTo} organizationId={organizationId} />

      <div className="field">
        <label className="field__label" htmlFor="register">
          Register
        </label>
        <textarea
          id="register"
          className="field__input register"
          rows={8}
          spellCheck={false}
          placeholder={EXAMPLE}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      <input
        ref={fileInput}
        type="file"
        accept=".csv,.tsv,.txt,text/csv,text/plain"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) setText(await file.text());
          e.target.value = '';
        }}
      />

      <div className="row">
        <button type="button" className="btn" onClick={() => fileInput.current?.click()}>
          Load a CSV instead
        </button>
        {text.trim() && (
          <button type="button" className="btn btn--ghost" onClick={() => setText('')}>
            Clear
          </button>
        )}
      </div>

      {rows.length > 0 && (
        <>
          <p className="muted">
            <span className="mono">{ready.length}</span> ready to enrol
            {broken.length > 0 && (
              <>
                {' · '}
                <span className="mono">{broken.length}</span>{' '}
                {broken.length === 1 ? 'needs' : 'need'} fixing first
              </>
            )}
          </p>

          {broken.length > 0 && (
            <ul className="diaglist">
              {broken.map((r) => (
                <li key={r.line} className="diaglist__item diaglist__item--error">
                  <span className="mono diaglist__line">line {r.line}</span>
                  <span>{r.error}</span>
                </li>
              ))}
            </ul>
          )}

          {ready.length > 0 && (
            <div className="ledger__wrap">
              <table className="ledger">
                <thead>
                  <tr>
                    <th>Name or code</th>
                    <th>Signs in with</th>
                    <th>Password</th>
                  </tr>
                </thead>
                <tbody>
                  {ready.slice(0, 8).map((r) => (
                    <tr key={r.line}>
                      <td style={{ fontWeight: 600 }}>{r.fullName}</td>
                      {/* The normalised form, so the teacher sees what will
                          actually be stored before committing to it. */}
                      <td className="mono muted">
                        {r.normalised ? formatPhone(r.normalised) : 'their candidate number'}
                      </td>
                      <td className="muted">{r.password ? 'as given' : 'generated'}</td>
                    </tr>
                  ))}
                  {ready.length > 8 && (
                    <tr>
                      <td colSpan={3} className="muted">
                        …and {ready.length - 8} more
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="row">
        <button
          className="btn btn--primary"
          disabled={busy || ready.length === 0}
          onClick={() => void enrol()}
        >
          {busy
            ? 'Enrolling…'
            : `Enrol ${ready.length || ''} student${ready.length === 1 ? '' : 's'}` +
              (groupName.trim() ? ` into ${groupName.trim()}` : '')}
        </button>
        {broken.length > 0 && ready.length > 0 && (
          <span className="field__hint">
            The {broken.length} line{broken.length === 1 ? '' : 's'} above will be left out.
          </span>
        )}
      </div>
    </section>
  );
}
